import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, ShieldCheck, MapPin, Check, Package, HandHeart, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import SEO from "../components/SEO";
import { CATEGORY_META } from "./Services";

const ServiceDetail = () => {
    const { id } = useParams();
    const [svc, setSvc] = useState(null);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [success, setSuccess] = useState(null);
    const [form, setForm] = useState({
        requester_name: "",
        requester_email: "",
        requester_phone: "",
        requester_address: "",
        quantity: 1,
        message: "",
    });

    useEffect(() => {
        api.get(`/services/${id}`)
            .then((r) => setSvc(r.data))
            .catch(() => toast.error("Listing not found"))
            .finally(() => setLoading(false));
    }, [id]);

    const submit = async (e) => {
        e.preventDefault();
        const q = parseInt(form.quantity, 10);
        if (!q || q < 1) { toast.error("Quantity must be at least 1"); return; }
        if (q > svc.quantity_available) { toast.error(`Only ${svc.quantity_available} ${svc.unit_label} left`); return; }
        setSubmitting(true);
        try {
            const { data } = await api.post(`/services/${id}/order`, { ...form, quantity: q });
            setSuccess(data);
            toast.success("Sent!");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not submit");
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return <div className="min-h-screen bg-[#FDFBF7] py-20 text-center text-sm text-[#4A5568]">Loading…</div>;
    }
    if (!svc) {
        return (
            <div className="min-h-screen bg-[#FDFBF7] py-20 text-center">
                <p className="font-heading text-2xl text-[#1C252E]">Listing not found.</p>
                <Link to="/services" className="text-[#E53935] text-sm mt-3 inline-block hover:underline">← Back to the board</Link>
            </div>
        );
    }

    const meta = CATEGORY_META[svc.category] || CATEGORY_META.other;
    const Icon = svc.kind === "request" ? HandHeart : Package;
    const verbNoun = svc.kind === "request" ? "Offer to help" : "Request this";

    return (
        <div className="min-h-screen bg-[#FDFBF7]" data-testid="service-detail">
            <SEO title={`${svc.title} — Community board`} description={svc.description.slice(0, 160)} path={`/services/${svc.id}`} />
            <div className="max-w-5xl mx-auto px-6 sm:px-8 lg:px-12 py-12">
                <Link to="/services" className="inline-flex items-center gap-1 text-sm text-[#4A5568] hover:text-[#E53935] mb-6">
                    <ArrowLeft className="w-4 h-4" strokeWidth={2} /> Back to the board
                </Link>

                <header className="mb-8">
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full ${svc.kind === "request" ? "bg-amber-50 text-amber-800" : "bg-green-100 text-green-800"}`}>
                            {svc.kind === "request" ? "Need" : "Free offer"}
                        </span>
                        <span className="text-[10px] uppercase tracking-wider font-bold text-[#4A5568]">{meta.label}</span>
                    </div>
                    <h1 className="font-heading text-4xl sm:text-5xl text-[#1C252E] font-medium tracking-tight leading-[1.05] mt-3">{svc.title}</h1>
                    <p className="text-sm text-[#4A5568] mt-2 flex items-center gap-1.5">
                        <MapPin className="w-4 h-4" strokeWidth={1.5} /> {svc.lister_first_name}{svc.town ? `, ${svc.town}` : ""}{svc.business_name ? ` · ${svc.business_name}` : ""}
                    </p>
                </header>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    <aside className="lg:col-span-1 space-y-4">
                        {svc.images?.length > 0 ? (
                            <div className="grid grid-cols-1 gap-3">
                                {svc.images.slice(0, 3).map((src, i) => (
                                    <img key={i} src={src} alt="" className="w-full rounded-2xl object-cover max-h-64" />
                                ))}
                            </div>
                        ) : (
                            <div className="bg-[#F2D8D5]/40 rounded-2xl p-12 flex items-center justify-center">
                                <Icon className="w-16 h-16 text-[#E53935]" strokeWidth={1} />
                            </div>
                        )}
                        <div className="bg-white border border-black/10 rounded-2xl p-5 space-y-2 text-sm">
                            <p className="flex items-center justify-between"><span className="text-[#4A5568]">Available</span><strong className="text-[#1C252E]">{svc.quantity_available} {svc.unit_label}</strong></p>
                            <p className="flex items-center justify-between"><span className="text-[#4A5568]">Total posted</span><strong className="text-[#1C252E]">{svc.quantity_total} {svc.unit_label}</strong></p>
                            <p className="flex items-center justify-between"><span className="text-[#4A5568]">Posted</span><strong className="text-[#1C252E] text-xs">{svc.approved_at ? new Date(svc.approved_at).toLocaleDateString() : "—"}</strong></p>
                        </div>
                        <div className="bg-[#F4F1EA] rounded-2xl p-5 text-xs text-[#4A5568] space-y-2">
                            <p className="flex items-center gap-1.5 text-[#1C252E] font-bold uppercase tracking-wider"><ShieldCheck className="w-3.5 h-3.5 text-[#E53935]" /> How this works</p>
                            <p>1. You provide your name + contact info — we pass it directly to <strong>{svc.lister_first_name}</strong>.</p>
                            <p>2. They&apos;ll reach out to coordinate pickup / delivery.</p>
                            <p>3. <strong>No money changes hands here.</strong> If they ask for gas reimbursement or similar, that&apos;s between the two of you.</p>
                            <p>4. The foundation is CC&apos;d on the request email purely for transparency / safety.</p>
                        </div>
                    </aside>

                    <main className="lg:col-span-2">
                        <article className="bg-white border border-black/10 rounded-2xl p-6 sm:p-8 mb-6">
                            <h2 className="font-heading text-xl text-[#1C252E] mb-3">Details</h2>
                            <p className="text-[#1C252E] whitespace-pre-line leading-relaxed">{svc.description}</p>
                        </article>

                        {success ? (
                            <div className="bg-white border border-green-200 rounded-2xl p-8 text-center" data-testid="service-success">
                                <div className="w-12 h-12 rounded-full bg-green-50 flex items-center justify-center mx-auto mb-4">
                                    <Check className="w-6 h-6 text-green-700" strokeWidth={2} />
                                </div>
                                <h2 className="font-heading text-2xl text-[#1C252E]">Sent — they&apos;ll be in touch</h2>
                                <p className="text-sm text-[#4A5568] mt-3 max-w-md mx-auto">{success.message}</p>
                                <p className="text-base text-[#1C252E] font-medium mt-4">Confirmation: <span className="text-[#E53935] font-mono text-sm">{success.order_id}</span></p>
                                {success.lister_email && (
                                    <p className="text-xs text-[#4A5568] mt-3">You can also reach {success.lister_first_name} directly at <a href={`mailto:${success.lister_email}`} className="text-[#E53935] hover:underline">{success.lister_email}</a></p>
                                )}
                                <Link to="/services" className="inline-block mt-6 text-sm text-[#E53935] hover:underline">← Back to the board</Link>
                            </div>
                        ) : svc.quantity_available < 1 ? (
                            <div className="bg-white border border-black/10 rounded-2xl p-8 text-center" data-testid="service-closed">
                                <AlertCircle className="w-10 h-10 text-[#4A5568] mx-auto mb-3" strokeWidth={1.5} />
                                <p className="font-heading text-xl text-[#1C252E]">All gone — this listing has been fully claimed.</p>
                                <Link to="/services" className="inline-block mt-4 text-sm text-[#E53935] hover:underline">See other listings →</Link>
                            </div>
                        ) : (
                            <form onSubmit={submit} className="bg-white border border-black/10 rounded-2xl p-6 sm:p-8 space-y-4" data-testid="service-order-form">
                                <h2 className="font-heading text-2xl text-[#1C252E] tracking-tight">{verbNoun}</h2>
                                <p className="text-xs text-[#4A5568] -mt-2">Your details are shared only with the lister and the foundation (CC&apos;d for transparency).</p>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    <div>
                                        <label htmlFor="sv-name" className="text-xs uppercase tracking-wider font-bold text-[#4A5568]">Your name *</label>
                                        <input id="sv-name" data-testid="service-order-name" required value={form.requester_name} onChange={(e) => setForm({ ...form, requester_name: e.target.value })} className="mt-1 w-full rounded-xl border border-black/10 px-4 py-2.5 bg-white focus:outline-none focus:ring-2 focus:ring-[#E53935]/40 focus:border-[#E53935]" />
                                    </div>
                                    <div>
                                        <label htmlFor="sv-email" className="text-xs uppercase tracking-wider font-bold text-[#4A5568]">Email *</label>
                                        <input id="sv-email" type="email" data-testid="service-order-email" required value={form.requester_email} onChange={(e) => setForm({ ...form, requester_email: e.target.value })} className="mt-1 w-full rounded-xl border border-black/10 px-4 py-2.5 bg-white focus:outline-none focus:ring-2 focus:ring-[#E53935]/40 focus:border-[#E53935]" />
                                    </div>
                                    <div>
                                        <label htmlFor="sv-phone" className="text-xs uppercase tracking-wider font-bold text-[#4A5568]">Phone *</label>
                                        <input id="sv-phone" data-testid="service-order-phone" required value={form.requester_phone} onChange={(e) => setForm({ ...form, requester_phone: e.target.value })} className="mt-1 w-full rounded-xl border border-black/10 px-4 py-2.5 bg-white focus:outline-none focus:ring-2 focus:ring-[#E53935]/40 focus:border-[#E53935]" />
                                    </div>
                                    <div>
                                        <label htmlFor="sv-qty" className="text-xs uppercase tracking-wider font-bold text-[#4A5568]">How many {svc.unit_label}?</label>
                                        <input id="sv-qty" type="number" min="1" max={svc.quantity_available} data-testid="service-order-qty" required value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} className="mt-1 w-full rounded-xl border border-black/10 px-4 py-2.5 bg-white focus:outline-none focus:ring-2 focus:ring-[#E53935]/40 focus:border-[#E53935]" />
                                    </div>
                                </div>
                                <div>
                                    <label htmlFor="sv-addr" className="text-xs uppercase tracking-wider font-bold text-[#4A5568]">Your address *</label>
                                    <textarea id="sv-addr" rows={2} data-testid="service-order-address" required value={form.requester_address} onChange={(e) => setForm({ ...form, requester_address: e.target.value })} className="mt-1 w-full rounded-xl border border-black/10 px-4 py-2.5 bg-white focus:outline-none focus:ring-2 focus:ring-[#E53935]/40 focus:border-[#E53935]" placeholder="So they know if you're nearby" />
                                </div>
                                <div>
                                    <label htmlFor="sv-msg" className="text-xs uppercase tracking-wider font-bold text-[#4A5568]">Message (optional)</label>
                                    <textarea id="sv-msg" rows={3} data-testid="service-order-message" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="mt-1 w-full rounded-xl border border-black/10 px-4 py-2.5 bg-white focus:outline-none focus:ring-2 focus:ring-[#E53935]/40 focus:border-[#E53935]" placeholder="When's convenient for pickup? Anything else they should know?" />
                                </div>
                                <button type="submit" disabled={submitting} data-testid="service-order-submit" className="inline-flex items-center gap-2 bg-[#E53935] hover:bg-[#D32F2F] disabled:opacity-60 text-white rounded-full px-6 py-2.5 text-sm font-medium transition">
                                    {submitting ? "Sending…" : `Send my request →`}
                                </button>
                            </form>
                        )}
                    </main>
                </div>
            </div>
        </div>
    );
};

export default ServiceDetail;
